import axios from "axios";
import { parseISO } from "date-fns";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { ExternalAccountId, FartherAccountId, PhysicalAccountId, TransferId, } from "../../Types";
import ValidationError from "../../ZodError";
const ACHStatus = z
    .enum([
    "CANCELED",
    "COMPETE", // typo from server, transformed to COMPLETE below
    "ERROR",
    "PENDING_CANCELLATION",
    "PENDING_INITIATION",
    "PENDING_LIQUIDATION",
    "PENDING_PROCESSING",
    "RETURNED",
])
    .transform((val) => {
    // Since we are not sending this back to the server (status is read only),
    // it is safe to transform it for spelling
    switch (val) {
        case "COMPETE":
            return "COMPLETE";
        default:
            return val;
    }
});
const ACHDirection = z.enum(["DEPOSIT", "WITHDRAWAL"]);
export const custodianSpecificTransferStatus = [
    // Apex
    "Requested",
    "Validating",
    "Analyzing",
    "FundsPosted",
    "FundsPosting",
    "Reporting",
    "Returning",
    "FundsCanceling",
    "Postponed",
    "Postponing",
    "Batching",
    "Canceled",
    "ReviewPending",
    "RejectedValidationFailed",
    "RejectedUnapproved",
    "RejectedFundsNotPosted",
    "RejectedNotSentToBank",
    "Completed",
    "Returned",
    // Pershing
    "AWAITING_RELEASE_DATE",
    "CANCELLED",
    "PENDING_APPROVAL",
    "PENDING_ISSUANCE",
    "REJECTED",
    "REJECTED_BY_BANK",
    "PROCESSED",
];
const CustodianStatus = z.enum(custodianSpecificTransferStatus);
const CustodianDetails = z.object({
    apexAchId: z.string().optional(),
    status: CustodianStatus,
});
const ContributionType = z.enum(["REGULAR"]);
const IraContributionDetails = z.object({
    contributionType: ContributionType,
    contributionYear: z.number(),
});
const Transfer = z.object({
    createTime: z.string(),
    transferId: TransferId,
    direction: ACHDirection,
    isFullWithdrawal: z.boolean(),
    modifyTime: z.string(),
    status: ACHStatus,
    fundingAccountId: ExternalAccountId,
    physicalAccountId: PhysicalAccountId,
    virtualAccountId: FartherAccountId,
    amount: z.number().nullable(),
    custodianDetails: CustodianDetails.nullable(),
    iraContributionDetails: IraContributionDetails.nullable(),
});
export const Response = z.object({ transfers: Transfer.array() });
export const url = () => environment() === "PROD"
    ? "https://middleware.prod.farther.com/transfer/v1/transfers/ach"
    : "https://middleware.uat.farther.com/transfer/v1/transfers/ach";
export default async function getACH(config) {
    const { data } = await axios.get(url(), {
        headers: {
            Client: config.clientId,
            Authorization: `Bearer ${config.jwt}`,
            ...(config.advisorId && { Advisor: config.advisorId }),
        },
    });
    const decoded = Response.safeParse(data);
    if (decoded.success) {
        return decoded.data.transfers.map(({ virtualAccountId, createTime, modifyTime, ...transfer }) => ({
            ...transfer,
            fartherAccountId: virtualAccountId,
            createTime: parseISO(createTime),
            modifyTime: parseISO(modifyTime),
        }));
    }
    else {
        const errorMessage = "getACH returned unexpected data.";
        errorLog(getACH.name, JSON.stringify(decoded.error.issues));
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
//# sourceMappingURL=getACH.js.map